import React, { useEffect, useRef } from 'react'
import { useI18n } from 'i18n'

export const CODE_LENGTH = 6
export const emptyCode = () => Array(CODE_LENGTH).fill('')

/**
 * Six one-digit boxes for an emailed code. Typing advances, Backspace goes back,
 * pasting (or OS one-time-code autofill) fills every box. `onComplete` fires
 * when the last box is filled.
 */
const CodeInput = ({ digits, onChange, onComplete, error, disabled, autoFocus = true, legend }) => {
  const { t } = useI18n()
  const inputs = useRef([])

  useEffect(() => { if (autoFocus) inputs.current[0]?.focus() }, [autoFocus])
  // A cleared code (e.g. after a wrong attempt) puts the cursor back at the start.
  useEffect(() => { if (digits.every((d) => !d) && !disabled) inputs.current[0]?.focus() }, [digits, disabled])

  const fill = (start, text) => {
    const clean = text.replace(/\D/g, '')
    if (!clean) return
    const next = [...digits]
    for (let i = 0; i < clean.length && start + i < CODE_LENGTH; i++) next[start + i] = clean[i]
    onChange(next)
    inputs.current[Math.min(start + clean.length, CODE_LENGTH - 1)]?.focus()
    if (next.every(Boolean)) onComplete?.(next.join(''))
  }

  const onKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      const next = [...digits]
      next[index - 1] = ''
      onChange(next)
      inputs.current[index - 1]?.focus()
      event.preventDefault()
    } else if (event.key === 'ArrowLeft' && index > 0) {
      inputs.current[index - 1]?.focus()
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputs.current[index + 1]?.focus()
    }
  }

  const onInput = (index) => (event) => {
    const clean = event.target.value.replace(/\D/g, '')
    if (!clean) {
      const next = [...digits]
      next[index] = ''
      onChange(next)
      return
    }
    // OS one-time-code autofill drops the whole code into one box.
    if (clean.length >= CODE_LENGTH) fill(0, clean)
    else fill(index, clean.slice(-1))
  }

  return (
    <fieldset>
      <legend className='mb-3 block w-full text-center text-sm font-medium text-slate-600'>{legend || t('verify.enterCode')}</legend>
      <div className='flex justify-center gap-2'>
        {digits.map((digit, i) => (
          <input key={i} ref={(el) => { inputs.current[i] = el }} value={digit}
            inputMode='numeric' autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={CODE_LENGTH} disabled={disabled}
            aria-label={t('verify.digit', { n: i + 1 })} aria-invalid={Boolean(error)}
            onChange={onInput(i)} onKeyDown={onKeyDown(i)}
            onPaste={(e) => { e.preventDefault(); fill(i, e.clipboardData.getData('text')) }}
            onFocus={(e) => e.target.select()}
            className={`h-12 w-10 rounded-lg border text-center text-lg font-semibold tabular-nums focus:outline-none sm:w-11 ${error ? 'border-status-failed' : 'border-slate-300 focus:border-brand'}`} />
        ))}
      </div>
    </fieldset>
  )
}

export default CodeInput
